import { forwardRef, type HTMLAttributes, useEffect, useImperativeHandle, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { createPortal } from 'react-dom'
import { CloseIcon } from '@/icons'
import { dialogVariants } from './Dialog.variants'

export interface DialogProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean
  onClose: () => void
  showCloseIcon?: boolean
  accentColor?: 'none' | 'yellow' | 'green' | 'blue' | 'pink'
}

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  (
    { open, onClose, showCloseIcon = true, accentColor = 'none', children, className, ...props },
    ref
  ) => {
    const dialogRef = useRef<HTMLDivElement>(null)

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- dialogRef is always assigned by React before imperative handle is used
    useImperativeHandle(ref, () => dialogRef.current!, [])

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose()
      }
      if (open) {
        document.addEventListener('keydown', handleKeyDown)
        document.body.style.overflow = 'hidden'
      }
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        document.body.style.overflow = ''
      }
    }, [open, onClose])

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
          onClose()
        }
      }

      if (open) {
        document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [open, onClose])

    if (!open) return null

    return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

        <div className={dialogVariants({ accentColor, className })} ref={dialogRef} {...props}>
          {showCloseIcon ? (
            <button
              className={twMerge(
                'absolute top-2 right-2 flex justify-center items-center',
                'w-9 h-9',
                'text-white',
                'rounded-xl bg-glass-overlay-light shadow-white-top-accent'
              )}
              onClick={onClose}
              type="button"
            >
              <CloseIcon className="w-2" />
            </button>
          ) : null}
          {children}
        </div>
      </div>,
      document.body
    )
  }
)

Dialog.displayName = 'Dialog'
